import { resolveStoredFileUrl } from "utils/storedFileUrl";
import { gql, useLazyQuery, useSubscription } from "@apollo/client";
import { Card, Collapse, Fade, Grid, Icon, Slide, Tooltip } from "@mui/material";
import logo from "assets/images/comedor.png";
import SinDatos from "components/Carteles/SinDatos";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import PropTypes from "prop-types";
import { useContext, useEffect, useRef, useState } from "react";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto, makeNiceText } from "utils/formatters";
import {
  getVoucherDisponibleCafeteria,
  isProductoCompatibleConVoucher,
  validarAgregadoConVoucher,
} from "../../utils/voucherCafeteria";

function Cafeteria({ fechaPedido, token, idCliente, carrito, setCarrito, voucherRestriccion = null }) {
  const [categorias, setCategorias] = useState([]);
  const [currentProducto, setCurrentProducto] = useState(null);
  const [expanded, setExpanded] = useState([]);
  const [carritoVisible, setCarritoVisible] = useState(true);
  const carritoRef = useRef(null);
  const [successTimeout, setSuccessTimeout] = useState(null);
  const [iconKey, setIconKey] = useState(0);
  const [stockRefreshTick, setStockRefreshTick] = useState(0);
  const [disponibilidadOperativaMap, setDisponibilidadOperativaMap] = useState({});
  const { handleSnackbar } = useContext(MessageManager);
  const idSucursalStock = Number(localStorage.getItem("idSede") || 1);

  const visibleCarrito = carrito.filter((item) => !item.eliminado);
  const total = visibleCarrito.reduce((acc, item) => acc + item.precioCarta * item.cantidad, 0);
  const disponibleVoucher = getVoucherDisponibleCafeteria(voucherRestriccion);

  const [getProductos, { loading }] = useLazyQuery(
    gql`
      query getProductos($order: Order, $filter: JSON, $token: String, $tipoCategoria: String) {
        productos(order: $order, filter: $filter, token: $token, tipoCategoria: $tipoCategoria) {
          id
          nombre
          disponibilidad
          precioCarta
          descripcion
          combo
          esElaborado
          tipoProducto {
            id
            tipo
          }
          categoriaProducto {
            id
            categoria
          }
          ingredientes {
            id
          }
          productosCombo {
            id
            nombre
          }
          files {
            file
          }
        }
      }
    `
  );

  const [validarDisponibilidadProducto] = useLazyQuery(
    gql`
      query validarDisponibilidadProducto(
        $idProducto: ID!
        $cantidad: Int!
        $esElaborado: Boolean!
        $idSucursal: ID
        $token: String
      ) {
        validarDisponibilidadProducto(
          idProducto: $idProducto
          cantidad: $cantidad
          esElaborado: $esElaborado
          idSucursal: $idSucursal
          token: $token
        ) {
          ok
          mensaje
        }
      }
    `,
    { fetchPolicy: "no-cache" }
  );

  const [validarDisponibilidadProductos] = useLazyQuery(
    gql`
      query validarDisponibilidadProductos(
        $productos: [DisponibilidadProductoBatchInput!]!
        $idSucursal: ID
        $token: String
      ) {
        validarDisponibilidadProductos(
          productos: $productos
          idSucursal: $idSucursal
          token: $token
        ) {
          idProducto
          ok
        }
      }
    `,
    { fetchPolicy: "no-cache" }
  );

  const { data: nuevoPedidoEvent } = useSubscription(
    gql`
      subscription cafeteriaNuevoPedido($idCliente: ID!) {
        nuevoPedido(idCliente: $idCliente) {
          id
          updatedAt
        }
      }
    `,
    { variables: { idCliente }, skip: !idCliente }
  );

  const { data: pedidoModificadoEvent } = useSubscription(
    gql`
      subscription cafeteriaPedidoModificado($idCliente: ID!) {
        pedidoModificado(idCliente: $idCliente) {
          id
          updatedAt
        }
      }
    `,
    { variables: { idCliente }, skip: !idCliente }
  );

  useEffect(() => {
    if (nuevoPedidoEvent?.nuevoPedido || pedidoModificadoEvent?.pedidoModificado) {
      setStockRefreshTick((prev) => prev + 1);
    }
  }, [nuevoPedidoEvent, pedidoModificadoEvent]);

  useEffect(() => {
    getProductos({
      variables: {
        order: { field: "nombre", order: "ASC" },
        token,
        filter: {
          disponibilidad: true,
          precioCarta: { _gt: 0 },
          lugarVenta: { _substring: 'APP' },
        },
        tipoCategoria: null,
      },
    }).then((res) => {
      const productos = (res.data.productos || []).filter((producto) =>
        isProductoCompatibleConVoucher(producto, voucherRestriccion)
      );
      let newCategorias = [];
      productos.forEach((producto) => {
        if (!producto.categoriaProducto) return;
        const categoria = producto.categoriaProducto.categoria;
        const index = newCategorias.findIndex((cat) => cat.categoria === categoria);
        if (index === -1) {
          newCategorias.push({
            categoria,
            productos: [producto],
          });
        } else {
          newCategorias[index].productos.push(producto);
        }
      });
      setCategorias(newCategorias);
    });
  }, [fechaPedido, token, stockRefreshTick, getProductos, voucherRestriccion]);

  useEffect(() => {
    setCurrentProducto(null);
  }, [voucherRestriccion?.id]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setCarritoVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (carritoRef.current) {
      observer.observe(carritoRef.current);
    }

    return () => {
      if (carritoRef.current) {
        observer.unobserve(carritoRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const productosUnicos = new Map();
    (categorias || []).forEach((categoria) => {
      (categoria?.productos || []).forEach((producto) => {
        if (!producto?.id || productosUnicos.has(Number(producto.id))) return;
        productosUnicos.set(Number(producto.id), producto);
      });
    });

    if (productosUnicos.size === 0) {
      setDisponibilidadOperativaMap({});
      return;
    }

    const cantidadesCarrito = (visibleCarrito || []).reduce((acc, item) => {
      const idProd = Number(item?.id);
      if (!idProd) return acc;
      acc[idProd] = (acc[idProd] || 0) + Number(item?.cantidad || 0);
      return acc;
    }, {});

    const productosBatch = [...productosUnicos.values()].map((producto) => ({
      idProducto: Number(producto.id),
      cantidad: Number(cantidadesCarrito[Number(producto.id)] || 0) + 1,
      esElaborado: !!producto.esElaborado,
    }));

    let activo = true;
    validarDisponibilidadProductos({
      variables: {
        productos: productosBatch,
        idSucursal: idSucursalStock,
        token,
      },
    })
      .then(({ data }) => {
        if (!activo) return;
        const mapa = {};
        for (const item of data?.validarDisponibilidadProductos || []) {
          mapa[Number(item.idProducto)] = !!item.ok;
        }
        setDisponibilidadOperativaMap(mapa);
      })
      .catch(() => {
        if (!activo) return;
        setDisponibilidadOperativaMap({});
      });

    return () => {
      activo = false;
    };
  }, [
    categorias,
    carrito,
    idSucursalStock,
    token,
    stockRefreshTick,
    validarDisponibilidadProductos,
  ]);

  return (
    <>
      <Fade in={loading} unmountOnExit>
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          minHeight="50vh"
        >
          <SoftBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <SoftBox
              component="img"
              src={logo}
              alt="UPrO Logo"
              width="10rem"
              p={3}
              sx={{
                animation: "blinking 1.5s infinite",
                "@keyframes blinking": {
                  "0%": {
                    opacity: 1,
                  },
                  "50%": {
                    opacity: 0.5,
                  },
                  "100%": {
                    opacity: 1,
                  },
                },
              }}
            />
            <SoftTypography variant="h6" align="center">
              Estamos buscando los productos...
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </Fade>
      <Fade in={!loading}>
        <SoftBox display="flex" flexDirection="column" gap={2}>
          {voucherRestriccion && (
            <Card sx={{ border: "1px solid rgba(7, 154, 157, 0.32)", background: "#f4fffd" }}>
              <SoftBox p={2} display="flex" alignItems="center" gap={1.5}>
                <Icon sx={{ color: "#079a9d" }}>redeem</Icon>
                <SoftBox>
                  <SoftTypography variant="h6" fontWeight="bold">
                    {voucherRestriccion.nombreVoucher ||
                      voucherRestriccion.grupo?.nombre ||
                      "Voucher seleccionado"}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="secondary">
					{voucherRestriccion.tipo === "MONTO"
					  ? `El voucher cubre hasta $ ${formatMoneyPunto(disponibleVoucher)}; el excedente se paga con otro medio.`
                      : `Podés agregar hasta ${disponibleVoucher.toLocaleString("es-AR")} unidades compatibles.`}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </Card>
          )}
          {!loading && categorias.length === 0 && (
            <SinDatos
              icono="production_quantity_limits"
              titulo="No hay productos compatibles"
              mensaje="No encontramos productos disponibles para el voucher seleccionado"
            />
          )}
          {categorias.map((categoria) => (
            <Card key={categoria.categoria} sx={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}>
              <SoftBox display="flex" flexDirection="column" gap={2} p={2}>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  onClick={() => {
                    setExpanded((prev) =>
                      prev.includes(categoria.categoria)
                        ? prev.filter((cat) => cat !== categoria.categoria)
                        : [...prev, categoria.categoria]
                    );
                  }}
                  sx={{
                    cursor: "pointer",
                    zIndex: 1,
                  }}
                >
                  <SoftBox display="flex" flexDirection="column">
                    <SoftTypography variant="h6">{categoria.categoria}</SoftTypography>
                    <SoftTypography variant="caption">
                      {categoria.productos.length} productos
                    </SoftTypography>
                  </SoftBox>
                  <Tooltip
                    title={expanded.includes(categoria.categoria) ? "Ocultar" : "Mostrar"}
                    placement="top"
                  >
                    <SoftButton color="primary" circular iconOnly>
                      <Icon
                        sx={{
                          rotate: expanded.includes(categoria.categoria) ? "180deg" : "0deg",
                          transition: "all 0.5s",
                        }}
                      >
                        expand_more
                      </Icon>
                    </SoftButton>
                  </Tooltip>
                </SoftBox>
                <Collapse in={expanded.includes(categoria.categoria)} unmountOnExit mountOnEnter>
                  <Grid container spacing={3} pt={1}>
                    {categoria.productos.map((producto) => {
                      const sinStockOperativo =
                        disponibilidadOperativaMap[Number(producto.id)] === false;
                      const cantidadSeleccionada =
                        currentProducto?.id === producto.id
                          ? Number(currentProducto?.cantidad || 1)
                          : 1;
                      const validacionAgregar = validarAgregadoConVoucher({
                        voucher: voucherRestriccion,
                        carrito: visibleCarrito,
                        producto,
                        cantidad: cantidadSeleccionada,
                      });
                      const validacionIncrementar = validarAgregadoConVoucher({
                        voucher: voucherRestriccion,
                        carrito: visibleCarrito,
                        producto,
                        cantidad: cantidadSeleccionada + 1,
                      });
                      return (
                      <Grid item xs={12} sm={6} md={4} key={producto.id}>
                        <Card
                          sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            border: "1px solid rgba(0, 0, 0, 0.12)",
                            transition: "transform 0.3s ease-in-out",
                            "&:hover": {
                              transform: "scale(1.02)",
                            },
                          }}
                        >
                          {producto.files && producto.files.length > 0 ? (
                            <SoftBox
                              sx={{
                                position: "relative",
                                width: "100%",
                                paddingBottom: "100%",
                                overflow: "hidden",
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              <SoftBox
                                component="img"
                                src={resolveStoredFileUrl(producto.files[0].file)}
                                alt={producto.nombre}
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  objectPosition: "center",
                                  transition: "transform 0.3s ease-in-out",
                                  cursor: "pointer",
                                  "&:hover": {
                                    transform: "scale(1.05)",
                                  },
                                }}
                              />
                            </SoftBox>
                          ) : (
                            <SoftBox
                              sx={{
                                position: "relative",
                                width: "100%",
                                paddingBottom: "100%",
                                overflow: "hidden",
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              <SoftBox
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <SoftBox
                                  component="img"
                                  src={logo}
                                  alt={producto.nombre}
                                  sx={{
                                    width: "50%",
                                    height: "50%",
                                    objectFit: "contain",
                                    transition: "transform 0.3s ease-in-out",
                                    cursor: "pointer",
                                    "&:hover": {
                                      transform: "scale(1.05)",
                                    },
                                  }}
                                />
                              </SoftBox>
                            </SoftBox>
                          )}
                          <SoftBox
                            display="flex"
                            gap={2}
                            justifyContent="space-between"
                            alignItems="center"
                            p={2}
                          >
                            <SoftBox display="flex" flexDirection="column">
                              <SoftTypography variant="h6">
                                {makeNiceText(producto.nombre)}
                              </SoftTypography>
                              {producto?.tipoProducto?.tipo && (
                                <SoftTypography variant="h7" fontWeight="regular">
                                  {producto.tipoProducto.tipo}
                                </SoftTypography>
                              )}
                            </SoftBox>
                            <SoftBox>
                              <SoftBadge
                                color="primary"
                                badgeContent={`$${formatMoneyPunto(producto.precioCarta)}`}
                                container
                              />
                            </SoftBox>
                          </SoftBox>
                          <SoftBox
                            p={2}
                            pt={0}
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                            gap={2}
                          >
                            <SoftButton
                              color="primary"
                              circular
                              iconOnly
                              onClick={() => {
                                if (currentProducto?.id === producto.id) {
                                  setCurrentProducto({
                                    ...currentProducto,
                                    cantidad:
                                      currentProducto.cantidad > 1
                                        ? currentProducto.cantidad - 1
                                        : 1,
                                  });
                                } else {
                                  setCurrentProducto({
                                    id: producto.id,
                                    cantidad: 1,
                                  });
                                }
                              }}
                            >
                              <Icon>remove</Icon>
                            </SoftButton>
                            <SoftTypography variant="h7" fontWeight="regular">
                              {currentProducto?.id === producto.id ? currentProducto.cantidad : 1}
                            </SoftTypography>
                            <SoftButton
                              color="uproYellow"
                              circular
                              iconOnly
                              disabled={sinStockOperativo || !validacionIncrementar.permitido}
                              onClick={() => {
                                if (currentProducto?.id === producto.id) {
                                  setCurrentProducto({
                                    ...currentProducto,
                                    cantidad: currentProducto.cantidad + 1,
                                  });
                                } else {
                                  setCurrentProducto({
                                    id: producto.id,
                                    cantidad: 1,
                                  });
                                }
                              }}
                            >
                              <Icon>add</Icon>
                            </SoftButton>
                            <SoftButton
                              color="uproGreen"
                              circular
                              fullWidth
                              disabled={
                                sinStockOperativo ||
                                producto?.precioCarta <= 0 ||
                                !validacionAgregar.permitido
                              }
                              onClick={async () => {
                                const cantidadEnCarrito = (visibleCarrito || [])
                                  .filter((item) => Number(item?.id) === Number(producto.id))
                                  .reduce(
                                    (acc, item) => acc + Number(item?.cantidad || 0),
                                    0
                                  );
                                const cantidadAAgregar =
                                  currentProducto?.id === producto.id
                                    ? Number(currentProducto?.cantidad || 1)
                                    : 1;

                                const validacionVoucher = validarAgregadoConVoucher({
                                  voucher: voucherRestriccion,
                                  carrito: visibleCarrito,
                                  producto,
                                  cantidad: cantidadAAgregar,
                                });
                                if (!validacionVoucher.permitido) {
                                  handleSnackbar(validacionVoucher.mensaje, "warning");
                                  return;
                                }

                                try {
                                  const { data } = await validarDisponibilidadProducto({
                                    variables: {
                                      idProducto: producto.id,
                                      cantidad: cantidadEnCarrito + cantidadAAgregar,
                                      esElaborado: !!producto.esElaborado,
                                      idSucursal: idSucursalStock,
                                      token,
                                    },
                                  });

                                  if (!data?.validarDisponibilidadProducto?.ok) {
                                    setDisponibilidadOperativaMap((prev) => ({
                                      ...prev,
                                      [Number(producto.id)]: false,
                                    }));
                                    handleSnackbar(
                                      data?.validarDisponibilidadProducto?.mensaje ||
                                        "Stock agotado para este producto.",
                                      "error"
                                    );
                                    return;
                                  }
                                } catch (error) {
                                  handleSnackbar(
                                    "No se pudo validar stock en este momento.",
                                    "error"
                                  );
                                  return;
                                }

                                setCarrito((prev) => {
                                  const index = prev.findIndex(
                                    (item) => item.id === producto.id && !item.eliminado
                                  );
                                  if (index === -1) {
                                    return [
                                      ...prev,
                                      {
                                        ...producto,
                                        cantidad: cantidadAAgregar,
                                        uniqueId: `${producto.id}-${Date.now()}`,
                                      },
                                    ];
                                  } else {
                                    const newCarrito = [...prev];
                                    newCarrito[index].cantidad += cantidadAAgregar;
                                    return newCarrito;
                                  }
                                });
                                setCurrentProducto(null);
                                setIconKey((prev) => prev + 1);
                                const newTimeout = setTimeout(() => {
                                  setSuccessTimeout(null);
                                  setIconKey((prev) => prev + 1);
                                }, 3000);
                                if (successTimeout) {
                                  setIconKey((prev) => prev + 1);
                                  clearTimeout(successTimeout);
                                }
                                setSuccessTimeout(newTimeout);
                                //Colapsar la categoría al agregar un producto y desplazar el carrito
                                setExpanded((prev) =>
                                  prev.filter((cat) => cat !== categoria.categoria)
                                );
                                carritoRef.current?.scrollIntoView({ behavior: "smooth" });
                              }}
                            >
                              {sinStockOperativo
                                ? "Sin stock"
                                : !validacionAgregar.permitido
                                  ? "Límite alcanzado"
                                  : "Agregar"}
                              &nbsp;
                              {!sinStockOperativo && validacionAgregar.permitido && (
                                <Icon>add_shopping_cart</Icon>
                              )}
                            </SoftButton>
                          </SoftBox>
                        </Card>
                      </Grid>
                      );
                    })}
                  </Grid>
                </Collapse>
              </SoftBox>
            </Card>
          ))}
          <SoftBox ref={carritoRef}>
            <Card
              sx={{
                border: "1px solid rgba(0, 0, 0, 0.12)",
                zIndex: 2,
              }}
            >
              <SoftBox
                display="flex"
                justifyContent="space-between"
                px={2}
                py={1}
                sx={{
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                }}
              >
                <SoftBox display="flex" alignItems="center">
                  <SoftBox mr={1} display="flex" alignItems="center">
                    <Icon color="primary">shopping_cart</Icon>
                  </SoftBox>
                  <SoftTypography variant="h6" fontWeight="bold">
                    Carrito
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
              {carrito.filter((item) => !item.eliminado).length === 0 && (
                <SinDatos
                  icono="production_quantity_limits"
                  titulo="No hay productos"
                  mensaje="Agrega productos para continuar"
                />
              )}
              <Collapse
                in={visibleCarrito.length > 0}
                sx={{
                  maxHeight: {
                    xs: "100%",
                    sm: "50vh",
                  },
                  overflowY: "auto",
                }}
              >
                {carrito.map((producto, index) => (
                  <Slide
                    in={!producto.eliminado}
                    key={producto.uniqueId || index}
                    direction="right"
                    mountOnEnter
                    unmountOnExit
                  >
                    <SoftBox>
                      <SoftBox
                        px={2}
                        py={1}
                        sx={{
                          borderBottom:
                            index === carrito.length - 1 ||
                              producto.uniqueId ===
                              carrito.filter((item) => !item.eliminado).slice(-1)[0]?.uniqueId
                              ? "none"
                              : "1px solid rgba(0, 0, 0, 0.12)",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <SoftBox
                          display={{
                            xs: "flex-row",
                            sm: "flex",
                          }}
                          justifyContent="space-between"
                        >
                          <SoftBox
                            display="flex"
                            alignItems="center"
                            justifyContent={{
                              xs: "space-between",
                              sm: "flex-start",
                            }}
                          >
                            <SoftBox display="flex" alignItems="center">
                              <SoftBox
                                mr={{
                                  xs: 0,
                                  sm: 1,
                                }}
                                sx={{
                                  display: {
                                    xs: "none",
                                    sm: "block",
                                  },
                                }}
                              >
                                <Tooltip
                                  title={producto.categoriaProducto.categoria}
                                  placement="top"
                                >
                                  <Icon
                                    sx={{
                                      color: getUproColor("uproYellow"),
                                      cursor: "pointer",
                                    }}
                                  >
                                    restaurant_menu
                                  </Icon>
                                </Tooltip>
                              </SoftBox>
                              <SoftBox display="flex" gap={1} alignItems="center">
                                <SoftTypography variant="h6">
                                  {makeNiceText(producto.nombre)}
                                </SoftTypography>
                                <SoftBadge
                                  color="uproYellow"
                                  badgeContent={`x${producto.cantidad}`}
                                />
                              </SoftBox>
                            </SoftBox>
                          </SoftBox>
                          <SoftBox
                            display="flex"
                            justifyContent={{
                              xs: "space-between",
                              sm: "flex-end",
                            }}
                            alignItems="center"
                            gap={2}
                            mt={{
                              xs: 2,
                              sm: 0,
                            }}
                          >
                            <SoftBox display="flex" alignItems="center" gap={2}>
                              <SoftButton
                                color="primary"
                                circular
                                iconOnly
                                onClick={() => {
                                  const newCarrito = JSON.parse(JSON.stringify(carrito));
                                  newCarrito[index].cantidad =
                                    newCarrito[index].cantidad > 1
                                      ? newCarrito[index].cantidad - 1
                                      : 1;
                                  setCarrito(newCarrito);
                                }}
                              >
                                <Icon>remove</Icon>
                              </SoftButton>
                              <SoftTypography variant="h7" fontWeight="regular">
                                {producto.cantidad}
                              </SoftTypography>
                              <SoftButton
                                color="uproYellow"
                                circular
                                iconOnly
                                disabled={
                                  disponibilidadOperativaMap[Number(producto.id)] === false ||
                                  !validarAgregadoConVoucher({
                                    voucher: voucherRestriccion,
                                    carrito: visibleCarrito,
                                    producto,
                                    cantidad: 1,
                                  }).permitido
                                }
                                onClick={async () => {
                                  const cantidadActual = Number(producto?.cantidad || 0);
                                  const validacionVoucher = validarAgregadoConVoucher({
                                    voucher: voucherRestriccion,
                                    carrito: visibleCarrito,
                                    producto,
                                    cantidad: 1,
                                  });
                                  if (!validacionVoucher.permitido) {
                                    handleSnackbar(validacionVoucher.mensaje, "warning");
                                    return;
                                  }
                                  try {
                                    const { data } = await validarDisponibilidadProducto({
                                      variables: {
                                        idProducto: producto.id,
                                        cantidad: cantidadActual + 1,
                                        esElaborado: !!producto.esElaborado,
                                        idSucursal: idSucursalStock,
                                        token,
                                      },
                                    });

                                    if (!data?.validarDisponibilidadProducto?.ok) {
                                      setDisponibilidadOperativaMap((prev) => ({
                                        ...prev,
                                        [Number(producto.id)]: false,
                                      }));
                                      handleSnackbar(
                                        data?.validarDisponibilidadProducto?.mensaje ||
                                          "Stock agotado para este producto.",
                                        "error"
                                      );
                                      return;
                                    }
                                  } catch (error) {
                                    handleSnackbar(
                                      "No se pudo validar stock en este momento.",
                                      "error"
                                    );
                                    return;
                                  }

                                  const newCarrito = JSON.parse(JSON.stringify(carrito));
                                  newCarrito[index].cantidad += 1;
                                  setCarrito(newCarrito);
                                }}
                              >
                                <Icon>add</Icon>
                              </SoftButton>
                            </SoftBox>
                            <SoftBox display="flex" alignItems="center" gap={2}>
                              <SoftBox display="flex" flexDirection="column" alignItems="flex-end">
                                <SoftTypography
                                  variant="h6"
                                  color={"text"}
                                  sx={{
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  $ {formatMoneyPunto(producto.precioCarta * producto.cantidad)}
                                </SoftTypography>
                                <SoftTypography
                                  variant="caption"
                                  color={"text"}
                                  sx={{
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  $ {formatMoneyPunto(producto.precioCarta)} c/u
                                </SoftTypography>
                              </SoftBox>
                              <SoftBox>
                                <Tooltip title={"Quitar del carrito"} placement="top">
                                  <SoftButton
                                    color="primary"
                                    circular
                                    iconOnly
                                    fullWidth={{
                                      xs: true,
                                      sm: false,
                                    }}
                                    onClick={() => {
                                      const newCarrito = JSON.parse(JSON.stringify(carrito));
                                      newCarrito[index].eliminado = true;
                                      setCarrito(newCarrito);
                                    }}
                                  >
                                    <Icon>delete</Icon>
                                  </SoftButton>
                                </Tooltip>
                              </SoftBox>
                            </SoftBox>
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Slide>
                ))}
              </Collapse>
            </Card>
            <Card
              sx={{
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                position: "sticky",
                marginTop: -2,
                zIndex: 1,
              }}
            >
              <SoftBox bgColor="primary">
                <SoftBox display="flex" justifyContent="center" bgColor="primary" p={2} pt={4}>
                  <SoftTypography variant="h6" fontWeight="bold" color="white">
                    Total: $ {formatMoneyPunto(total)}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </Card>
          </SoftBox>
          {!carritoVisible && carrito?.filter((item) => !item.eliminado).length > 0 && (
            <Slide direction="up" in={!carritoVisible} mountOnEnter unmountOnExit>
              <SoftBox
                px={2}
                mb={2}
                sx={{
                  position: "sticky",
                  zIndex: 9999,
                  bottom: 0,
                  right: 0,
                  width: {
                    xs: "100%",
                    sm: "30%",
                  },
                  alignSelf: "flex-end",
                }}
              >
                <SoftButton
                  fullWidth
                  color={successTimeout ? "uproGreen" : "primary"}
                  circular
                  onClick={() => carritoRef.current?.scrollIntoView({ behavior: "smooth" })}
                  sx={{
                    transition: "background-color 0.3s ease",
                    animation: "float 3s ease-in-out infinite",
                    "@keyframes float": {
                      "0%": {
                        transform: "translateY(0)",
                      },
                      "50%": {
                        transform: "translateY(-10px)",
                      },
                      "100%": {
                        transform: "translateY(0)",
                      },
                    },
                  }}
                >
                  {successTimeout ? "Producto agregado al carrito" : "Ver carrito"}&nbsp;
                  <Icon
                    key={iconKey}
                    sx={{
                      animation: "shakes 1s",
                      "@keyframes shakes": {
                        "0%": {
                          transform: "rotate(0deg)",
                        },
                        "25%": {
                          transform: "rotate(10deg)",
                        },
                        "50%": {
                          transform: "rotate(-20deg)",
                        },
                        "75%": {
                          transform: "rotate(15deg)",
                        },
                        "100%": {
                          transform: "rotate(-5deg)",
                        },
                      },
                    }}
                  >
                    {successTimeout ? "done" : "shopping_cart"}
                  </Icon>
                </SoftButton>
              </SoftBox>
            </Slide>
          )}
        </SoftBox>
      </Fade>
    </>
  );
}

Cafeteria.propTypes = {
  idCliente: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fechaPedido: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  token: PropTypes.string.isRequired,
  carrito: PropTypes.array.isRequired,
  setCarrito: PropTypes.func.isRequired,
  voucherRestriccion: PropTypes.object,
};

export default Cafeteria;
